/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
import { storage } from '@guardian/libs';
import { SlotNames } from './types';
const localStorageKeyBase = 'gu.brazeMessageCache';
export const millisecondsBeforeExpiry = 1000 * 60 * 60 * 24; // 24 hours: 60 seconds * 60 minutes
const MAX_QUEUE_SIZE = 2;
const keyFromSlotName = (slotName) => `${localStorageKeyBase}.${slotName}`;
const hasNotExpired = (cachedMessage) => cachedMessage.expires > Date.now();
// setQueue is effectively private, but it's useful to expose it publicly
// so that we can use it in the tests
const setQueue = (slotName, queue) => {
    const key = keyFromSlotName(slotName);
    storage.local.set(key, queue);
};
const readQueue = (slotName) => {
    const key = keyFromSlotName(slotName);
    const queue = storage.local.get(key);
    if (Array.isArray(queue)) {
        return queue;
    }
    return [];
};
const hydrateMessage = (messageData, appboyInstance) => {
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    const hydratedMessage = new appboyInstance.HtmlMessage(messageData.message, messageData.extras, messageData.campaignId, messageData.cardId, messageData.triggerId, messageData.dismissType, messageData.duration, messageData.animateIn, messageData.animateOut, messageData.frameColor, messageData.htmlId, messageData.css, messageData.messageFields);
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    return hydratedMessage;
};
const isValid = (m) => {
    return (m?.expires &&
        Number.isFinite(m?.expires) &&
        m?.message?.id &&
        m?.message?.message?.triggerId &&
        m?.message?.message?.extras);
};
const getQueue = (slotName) => {
    const queue = readQueue(slotName);
    const validQueue = queue.filter((i) => isValid(i));
    const unexpiredQueue = validQueue.filter((i) => hasNotExpired(i));
    if (queue.length !== unexpiredQueue.length) {
        setQueue(slotName, unexpiredQueue);
    }
    return unexpiredQueue;
};
class LocalMessageCache {
    static peek(slotName, appboyInstance) {
        const queue = getQueue(slotName);
        const topItem = queue[0];
        if (topItem) {
            return {
                id: topItem.message.id,
                message: hydrateMessage(topItem.message.message, appboyInstance),
            };
        }
    }
    static remove(slotName, id) {
        const queue = getQueue(slotName);
        const idx = queue.findIndex((i) => i.message.id === id);
        if (idx >= 0) {
            const removedItem = queue.splice(idx, 1);
            if (removedItem) {
                setQueue(slotName, queue);
                return true;
            }
        }
        return false;
    }
    static push(slotName, message) {
        const queue = getQueue(slotName);
        if (queue.length < MAX_QUEUE_SIZE) {
            const expires = Date.now() + millisecondsBeforeExpiry;
            const messageToCache = {
                message,
                expires,
            };
            queue.push(messageToCache);
            setQueue(slotName, queue);
            return true;
        }
        return false;
    }
    static clear() {
        // eslint-disable-next-line guard-for-in
        for (const slotName in SlotNames) {
            const key = keyFromSlotName(slotName);
            storage.local.remove(key);
        }
    }
}
const inMemoryQueue = {
    EndOfArticle: [],
    Banner: [],
};
// Until we're ready to turn caching on we can use this as a swap in replacement
// for LocalMessageCache.
class InMemoryCache {
    static peek(slotName) {
        const unexpiredMessages = inMemoryQueue[slotName].filter((i) => hasNotExpired(i));
        return unexpiredMessages[0]?.message;
    }
    static remove(slotName, id) {
        const idx = inMemoryQueue[slotName].findIndex((i) => i.message.id === id);
        if (idx >= 0) {
            const removedItem = inMemoryQueue[slotName].splice(idx, 1);
            if (removedItem) {
                return true;
            }
        }
        return false;
    }
    static push(slotName, message) {
        if (inMemoryQueue[slotName].length < MAX_QUEUE_SIZE) {
            const expires = Date.now() + millisecondsBeforeExpiry;
            const messageToCache = {
                message,
                expires,
            };
            inMemoryQueue[slotName].push(messageToCache);
            return true;
        }
        return false;
    }
    static clear() {
        // eslint-disable-next-line guard-for-in
        for (const slotName in SlotNames) {
            inMemoryQueue[slotName] = [];
        }
    }
}
export { LocalMessageCache, InMemoryCache, setQueue, hydrateMessage, };
